import { useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useFtChatbotChat } from '../../hooks/api/mutations/ft-chatbot/useFtChatbotChat'
import { useFtChatbotConfirm } from '../../hooks/api/mutations/ft-chatbot/useFtChatbotConfirm'
import { useFtChatbotDeleteConversation } from '../../hooks/api/mutations/ft-chatbot/useFtChatbotDeleteConversation'
import { fetchChatbotConversation } from '../../hooks/api/queries/useFtChatbotConversations'
import { useLanguage } from '@context'
import { showApiErrorToast, translateApiError } from '@utils'

export interface PendingAction {
  toolName: string
  args: Record<string, unknown>
  description: string
  // The note or the person an update / delete is about (absent from older servers).
  targetLabel?: string
}

export interface ChatMessage {
  id: string
  role: 'user' | 'model'
  content: string
  timestamp: string
  pendingActions?: PendingAction[]
}

export const useChatbot = () => {
  const { t } = useLanguage()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputText, setInputText] = useState('')
  const [conversationId, setConversationId] = useState<string | undefined>()
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([])
  // The message whose card carries the pending actions: one live card at a
  // time, and its button sends exactly what it shows.
  const [pendingMessageId, setPendingMessageId] = useState<string | undefined>()
  const [isThinking, setIsThinking] = useState(false)

  const chatMutation = useFtChatbotChat()
  const confirmMutation = useFtChatbotConfirm()
  const deleteMutation = useFtChatbotDeleteConversation()
  const queryClient = useQueryClient()

  const sendMessage = useCallback(
    (text?: string) => {
      const messageText = text || inputText.trim()
      if (!messageText || isThinking) return

      const userMsg: ChatMessage = {
        id: Date.now().toString(),
        role: 'user',
        content: messageText,
        timestamp: new Date().toISOString(),
      }

      setMessages((prev) => [...prev, userMsg])
      setInputText('')
      setIsThinking(true)

      chatMutation.mutate(
        { message: messageText, conversationId },
        {
          onSuccess: (data) => {
            const aiMsg: ChatMessage = {
              id: (Date.now() + 1).toString(),
              role: 'model',
              content: data.response,
              timestamp: new Date().toISOString(),
              pendingActions: data.pendingActions,
            }
            setMessages((prev) => [...prev, aiMsg])
            setConversationId(data.conversationId)
            // A new conversation appears in the history.
            void queryClient.invalidateQueries({ queryKey: ['chatbot-conversations'] })
            if (data.pendingActions && data.pendingActions.length > 0) {
              setPendingActions(data.pendingActions)
              setPendingMessageId(aiMsg.id)
            }
            setIsThinking(false)
          },
          onError: (error) => {
            const errorMsg: ChatMessage = {
              id: (Date.now() + 1).toString(),
              role: 'model',
              content: translateApiError(t, error).message,
              timestamp: new Date().toISOString(),
            }
            setMessages((prev) => [...prev, errorMsg])
            setIsThinking(false)
          },
        },
      )
    },
    [inputText, isThinking, conversationId, chatMutation, queryClient],
  )

  const confirmAction = useCallback(() => {
    if (pendingActions.length === 0 || !conversationId) return

    setIsThinking(true)

    confirmMutation.mutate(
      {
        conversationId,
        actions: pendingActions.map((a) => ({ toolName: a.toolName, args: a.args })),
      },
      {
        onSuccess: (data) => {
          const confirmMsg: ChatMessage = {
            id: Date.now().toString(),
            role: 'model',
            content: data.response,
            timestamp: new Date().toISOString(),
            pendingActions: data.pendingActions,
          }
          setMessages((prev) => [...prev, confirmMsg])
          // If the continuation triggered more write tools, show them
          if (data.pendingActions && data.pendingActions.length > 0) {
            setPendingActions(data.pendingActions)
            setPendingMessageId(confirmMsg.id)
          } else {
            setPendingActions([])
            setPendingMessageId(undefined)
          }
          setIsThinking(false)
        },
        onError: (error) => {
          const errorMsg: ChatMessage = {
            id: Date.now().toString(),
            role: 'model',
            content: translateApiError(t, error).message,
            timestamp: new Date().toISOString(),
          }
          setMessages((prev) => [...prev, errorMsg])
          setPendingActions([])
          setPendingMessageId(undefined)
          setIsThinking(false)
        },
      },
    )
  }, [pendingActions, conversationId, confirmMutation])

  const cancelAction = useCallback(() => {
    const cancelMsg: ChatMessage = {
      id: Date.now().toString(),
      role: 'model',
      content: t('chatbot.actionCancelled'),
      timestamp: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, cancelMsg])
    setPendingActions([])
    setPendingMessageId(undefined)
  }, [t])

  const startNewConversation = useCallback(() => {
    setMessages([])
    setConversationId(undefined)
    setPendingActions([])
    setPendingMessageId(undefined)
    setInputText('')
    setIsThinking(false)
  }, [])

  // Reopens a saved conversation so it can be read and continued. An old
  // « Confirmer / Annuler » proposal is not shown again: it must not be
  // confirmed by mistake days later.
  const openConversation = useCallback(
    async (id: string) => {
      if (isThinking) return
      setIsThinking(true)
      try {
        const data = await fetchChatbotConversation(id)
        setMessages(
          data.messages.map((message, index) => ({
            id: `${id}-${index}`,
            role: message.role,
            content: message.content,
            timestamp: message.timestamp,
          })),
        )
        setConversationId(id)
        setPendingActions([])
        setPendingMessageId(undefined)
      } catch (error) {
        // The screen stays as it was (also against a server without this route).
        showApiErrorToast(t, error)
      } finally {
        setIsThinking(false)
      }
    },
    [isThinking, t],
  )

  const deleteConversation = useCallback(
    (id: string) => {
      deleteMutation.mutate(
        { id },
        {
          onSuccess: () => {
            if (id === conversationId) startNewConversation()
          },
        },
      )
    },
    [deleteMutation, conversationId, startNewConversation],
  )

  return {
    messages,
    conversationId,
    inputText,
    setInputText,
    isThinking,
    pendingActions,
    pendingMessageId,
    sendMessage,
    confirmAction,
    cancelAction,
    startNewConversation,
    openConversation,
    deleteConversation,
    hasDeleteFailed: deleteMutation.isError,
    clearDeleteFailure: deleteMutation.reset,
  }
}
